import React, { useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { FileText, Tag } from 'lucide-react';

import { accountNameOf } from '../accountName';
import { fetchAttachmentBlob } from '../api';
import { FIRST_REPLY_GREETING } from '../constants';
import { RATING_FACES, RatingFace } from '../ratings';
import { Conversation, ConversationDetail, Label, Macro, Message } from '../types';

import Composer from './Composer';
import LabelPicker from './LabelPicker';

const timeOf = (iso: string): string =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const dayOf = (iso: string): string =>
  new Date(iso).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });

/**
 * A kind=file row. The serving endpoint wants the agent's token, which
 * <img src> cannot send, so the bytes come over axios and the tag gets a blob
 * URL — revoked on unmount, because every one of these pins a file in memory.
 */
const FileEntry: React.FC<{ message: Message }> = ({ message }: { message: Message }) => {
  const attachment = message.meta?.attachment;
  const attachmentId = typeof attachment?.id === 'string' ? attachment.id : null;
  const contentType = typeof attachment?.content_type === 'string' ? attachment.content_type : '';
  const name = typeof attachment?.name === 'string' ? attachment.name : 'file';
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!attachmentId) return undefined;
    let active = true;
    let objectUrl: string | null = null;
    fetchAttachmentBlob(attachmentId)
      .then(({ data }) => {
        // Created only while mounted: a fetch that resolves after the cleanup
        // ran would mint a URL nobody is left to revoke.
        if (!active) return;
        objectUrl = URL.createObjectURL(data);
        setUrl(objectUrl);
      })
      .catch(() => {
        // The row still shows the filename; the bytes just aren't clickable.
      });
    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [attachmentId]);

  const fromUs = message.author_type !== 'contact';

  return (
    <div
      className={classNames(
        'max-w-4/5 lg:max-w-3/4 flex flex-col',
        fromUs ? 'self-end items-end' : 'self-start'
      )}
    >
      <div className="p-1 border rounded border-white-3.0 bg-white-0.0">
        {contentType.startsWith('image/') && url && (
          <img src={url} alt={name} className="max-h-60 block max-w-full rounded" />
        )}
        {contentType.startsWith('video/') && url && (
          <video controls src={url} className="max-h-60 block max-w-full rounded">
            <track kind="captions" />
          </video>
        )}
        {!contentType.startsWith('image/') && !contentType.startsWith('video/') && (
          <a
            href={url ?? undefined}
            target="_blank"
            rel="noreferrer"
            download={name}
            className="flex items-center gap-2 px-3 py-2 text-sm text-primary-2.0 hover:underline"
          >
            <FileText size={16} />
            {name}
          </a>
        )}
        {/* Media whose bytes haven't arrived (or failed) still says which
            file this was, instead of an empty frame. */}
        {(contentType.startsWith('image/') || contentType.startsWith('video/')) && !url && (
          <p className="px-3 py-2 text-sm text-gray-1.0">{name}</p>
        )}
      </div>
      <p className="mt-1 text-xs text-gray-1.0">
        {message.author_name || (fromUs ? 'BrainHi' : 'Customer')}
        {' · '}
        {timeOf(message.created_at)}
      </p>
    </div>
  );
};

/**
 * One row of the thread.
 *
 * A conversation carries more than prose: Lara's form and the marker left when
 * someone takes a conversation over are messages too. Drawing them as bubbles
 * would attribute the form's title to Lara as if she had said it, and label the
 * join marker "Customer" — so each kind gets the shape it actually is.
 *
 * Everything drawn from `meta` is treated as data off the wire rather than as
 * the shape the types promise: a value that isn't a string would otherwise
 * throw while rendering and take the whole thread down with it.
 */
const Entry: React.FC<{ message: Message }> = ({ message }: { message: Message }) => {
  if (message.kind === 'event') {
    return <p className="self-center text-xs text-center text-gray-1.0">{message.body}</p>;
  }

  if (message.kind === 'file') {
    return <FileEntry message={message} />;
  }

  // A sticky, not a bubble: it has to read as something the customer never
  // received, even skimming.
  if (message.kind === 'note') {
    return (
      <div className="max-w-4/5 lg:max-w-3/4 flex flex-col items-end self-end">
        <div className="px-4 py-2 rounded whitespace-pre-wrap break-words border bg-yellow-2.0 border-yellow-1.0 text-black-1.0">
          {message.body}
        </div>
        <p className="mt-1 text-xs text-gray-1.0">
          Internal note
          {' · '}
          {message.author_name || 'BrainHi'}
          {' · '}
          {timeOf(message.created_at)}
        </p>
      </div>
    );
  }

  if (message.kind === 'form') {
    const { submitted, fields = [] } = message.meta ?? {};
    const labels = new Map(fields.map((field) => [field.name, field.label]));

    // `submitted` is what was collected; `fields` only supplies the labels and
    // the order they were asked in. So every question appears — blank ones say
    // so rather than vanishing from a list the agent reads as complete — and
    // then anything answered that no question claims, because a key with no
    // label attached is still the only copy of what the customer told us.
    const names = submitted
      ? [
          ...fields.map((field) => field.name),
          ...Object.keys(submitted).filter((name) => !labels.has(name)),
        ]
      : [];

    return (
      <div className="self-start max-w-4/5 lg:max-w-3/4 px-3 py-2 text-xs border border-dashed rounded border-white-3.0 text-gray-3.0">
        <p className="font-medium">{message.author_name || 'BrainHi'} asked for details</p>
        {/* Three states, each telling the agent something different: still open,
            came back with nothing on it, or here is what it collected. */}
        {submitted == null && <p className="mt-1 text-gray-1.0">Not answered yet</p>}
        {submitted != null && names.length === 0 && (
          <p className="mt-1 text-gray-1.0">Answered, but nothing was recorded</p>
        )}
        {names.length > 0 && (
          <dl className="mt-1 space-y-0.5">
            {names.map((name) => (
              <div key={name} className="flex gap-1">
                <dt className="text-gray-1.0">{labels.get(name) ?? name}:</dt>
                <dd className="text-black-1.0">{String(submitted?.[name] ?? '') || '—'}</dd>
              </div>
            ))}
          </dl>
        )}
      </div>
    );
  }

  // Only the customer is the customer. Lara and any future system line are ours,
  // and a missing name on one of them must not put the customer's label on
  // something they never said. Sides mean who it came from: customer left,
  // everything ours right — the same reading telescope trains, where the
  // patient's column is the left one.
  const fromUs = message.author_type !== 'contact';
  const fromAgent = message.author_type === 'agent';
  const fallbackName = fromUs ? 'BrainHi' : 'Customer';

  return (
    <div
      className={classNames(
        'max-w-4/5 lg:max-w-3/4 flex flex-col',
        fromUs ? 'self-end items-end' : 'self-start'
      )}
    >
      <div
        className={classNames(
          'px-4 py-2 rounded whitespace-pre-wrap break-words',
          // Ours, but not a person: filled is a colleague who replied, outlined
          // is the bot. The agent has to be able to tell at a glance whether
          // this conversation has actually been answered.
          fromAgent && 'bg-primary-2.0 text-white-0.0',
          !fromUs && 'bg-white-1.0 text-black-1.0',
          fromUs && !fromAgent && 'border border-white-3.0 text-black-1.0'
        )}
      >
        {message.body}
      </div>
      <p className="mt-1 text-xs text-gray-1.0">
        {message.author_name || fallbackName}
        {' · '}
        {timeOf(message.created_at)}
      </p>
    </div>
  );
};

type Props = {
  conversation: ConversationDetail;
  history: Conversation[];
  isSending: boolean;
  labels: Label[];
  macros: Macro[];
  onOpenConversation: (id: string) => void;
  onSend: (body: string) => Promise<void>;
  onSendNote: (body: string) => Promise<void>;
  onSendFile: (file: File) => Promise<void>;
  onSetLabels: (labelIds: string[]) => Promise<void>;
  onCreateLabel: (name: string) => Promise<Label>;
  onManageLabels: () => void;
  onToggleState: () => void;
  onManageMacros: () => void;
};

const Thread: React.FC<Props> = ({
  conversation,
  history,
  isSending,
  labels,
  macros,
  onOpenConversation,
  onSend,
  onSendNote,
  onSendFile,
  onSetLabels,
  onCreateLabel,
  onManageLabels,
  onToggleState,
  onManageMacros,
}: Props) => {
  const bottom = useRef<HTMLLIElement>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);

  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
  }, [conversation.messages.length]);

  // Landing on another thread starts fresh: panels left open belong to the
  // conversation the agent just left.
  useEffect(() => {
    setIsPickerOpen(false);
    setIsHistoryOpen(false);
  }, [conversation.id]);

  const { contact } = conversation;
  const face = conversation.rating ? RATING_FACES[conversation.rating] : undefined;
  // Same reading as the takeover marker on the server: Lara doesn't count as an
  // answer, a colleague does — and neither does an internal note, which the
  // customer never received. Closed conversations don't get the seed — opening
  // one is reviewing, not answering.
  const needsGreeting =
    conversation.state === 'open' &&
    !conversation.messages.some(
      (message) => message.author_type === 'agent' && message.kind !== 'note'
    );
  // Promoted out of the trait list and into the title: who this is and which
  // practice they belong to is what the agent needs on screen the whole time,
  // not two rows deep in a context panel.
  const accountName = accountNameOf(contact);
  const traits = Object.entries(contact.traits ?? {}).filter(
    ([key, value]) =>
      value !== null && value !== '' && (accountName === null || key !== 'Account name')
  );

  // The selection lives here, optimistically: deriving it from the server
  // payload froze the checkboxes during each PUT's round-trip and made a
  // second quick toggle compute over the stale list, silently dropping the
  // first one. The server answer resyncs it below; a failed PUT reverts.
  const [selectedLabelIds, setSelectedLabelIds] = useState<string[]>(() =>
    conversation.labels.map((label) => label.id)
  );

  useEffect(() => {
    setSelectedLabelIds(conversation.labels.map((label) => label.id));
  }, [conversation.id, conversation.labels]);

  // Mirrors the state for the async path below: after an await, the closure's
  // copy of the selection predates any toggle that landed in the meantime.
  const selectedRef = useRef(selectedLabelIds);
  selectedRef.current = selectedLabelIds;

  const revertSelection = (): void =>
    setSelectedLabelIds(conversation.labels.map((label) => label.id));

  const toggleLabel = (labelId: string): void => {
    const next = selectedLabelIds.includes(labelId)
      ? selectedLabelIds.filter((id) => id !== labelId)
      : [...selectedLabelIds, labelId];
    setSelectedLabelIds(next);
    onSetLabels(next).catch(revertSelection);
  };

  // Creating from the picker means wanting it on this conversation: nobody
  // types a new label into an open thread just to file it for later.
  const createAndAssign = async (name: string): Promise<void> => {
    const label = await onCreateLabel(name);
    // Through the ref: a toggle during the create's round-trip must ride
    // along, not get overwritten by this closure's stale copy.
    const next = selectedRef.current.includes(label.id)
      ? selectedRef.current
      : [...selectedRef.current, label.id];
    setSelectedLabelIds(next);
    try {
      await onSetLabels(next);
    } catch {
      revertSelection();
    }
  };

  // Chips render from the optimistic selection, with names resolved from the
  // team list (and the conversation payload, for archived labels that left it).
  const labelNames = new Map<string, string>();
  labels.forEach((label) => labelNames.set(label.id, label.name));
  conversation.labels.forEach((label) => labelNames.set(label.id, label.name));
  const selectedLabels = selectedLabelIds.flatMap((id) => {
    const name = labelNames.get(id);
    return name === undefined ? [] : [{ id, name }];
  });

  return (
    <div className="flex flex-col flex-1 h-full min-w-0 overflow-hidden bg-white-0.0">
      <header className="flex items-center justify-between flex-shrink-0 gap-4 px-4 py-3 border-b border-white-2.0">
        <div className="min-w-0">
          <p className="font-semibold truncate text-black-1.0">
            {contact.name || 'Unknown'}
            {accountName && <span className="font-normal text-gray-3.0"> · {accountName}</span>}
          </p>
          <p className="text-sm truncate text-gray-1.0">{contact.email || 'No email'}</p>
          <div className="relative flex flex-wrap items-center gap-1 mt-1">
            {selectedLabels.map((label) => (
              <span
                key={label.id}
                className="px-2 py-0.5 text-xs rounded-full border border-white-3.0 bg-white-1.0 text-gray-3.0"
              >
                {label.name}
              </span>
            ))}
            <button
              type="button"
              aria-label="Labels"
              title="Labels"
              onClick={() => setIsPickerOpen((open) => !open)}
              className={classNames(
                'flex items-center gap-1 px-2 py-0.5 text-xs rounded-full border border-dashed transition-colors',
                isPickerOpen
                  ? 'border-primary-2.0 text-primary-2.0'
                  : 'border-white-3.0 text-gray-2.0 hover:text-primary-2.0'
              )}
            >
              <Tag size={12} />
              {selectedLabelIds.length === 0 ? 'Add label' : ''}
            </button>
            {isPickerOpen && (
              <LabelPicker
                labels={labels}
                selectedIds={selectedLabelIds}
                onToggle={toggleLabel}
                onCreate={createAndAssign}
                onManage={() => {
                  setIsPickerOpen(false);
                  onManageLabels();
                }}
                extraClassName="top-full mt-1 left-0"
              />
            )}
          </div>
          {traits.length > 0 && (
            <dl className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-gray-3.0">
              {traits.map(([key, value]) => (
                <div key={key} className="flex gap-1">
                  <dt className="text-gray-1.0">{key}:</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
          {history.length > 0 && (
            <div className="mt-1">
              <button
                type="button"
                onClick={() => setIsHistoryOpen((open) => !open)}
                className="text-xs font-medium text-primary-2.0 hover:underline"
              >
                {isHistoryOpen
                  ? 'Hide previous conversations'
                  : `${history.length} previous ${
                      history.length === 1 ? 'conversation' : 'conversations'
                    }`}
              </button>
              {isHistoryOpen && (
                <ul className="mt-1 overflow-y-auto border rounded max-h-40 border-white-2.0 divide-y divide-white-1.0">
                  {history.map((previous) => (
                    <li key={previous.id}>
                      <button
                        type="button"
                        onClick={() => onOpenConversation(previous.id)}
                        className="w-full px-3 py-1.5 text-left hover:bg-white-1.0"
                      >
                        <span className="flex items-baseline justify-between gap-2 text-xs">
                          <span className="text-gray-3.0">{dayOf(previous.last_message_at)}</span>
                          <span className="capitalize text-gray-1.0">{previous.state}</span>
                        </span>
                        <p className="text-sm truncate text-gray-1.0">
                          {previous.preview || 'No messages'}
                        </p>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        <div className="flex flex-col items-end flex-shrink-0 gap-1">
          <button
            type="button"
            onClick={onToggleState}
            className="px-3 py-1 text-sm border rounded border-white-3.0 text-gray-3.0 hover:bg-white-1.0"
          >
            {conversation.state === 'open' ? 'Close conversation' : 'Reopen'}
          </button>
          {face && (
            <p className="flex items-center gap-1.5 py-0.5 pl-1 pr-2.5 text-xs font-medium border rounded-full border-white-2.0 bg-white-1.0 text-gray-3.0">
              <span className="sr-only">Customer rating:</span>
              <RatingFace rating={conversation.rating ?? 0} size={22} labelled={false} />
              {face.label}
            </p>
          )}
        </div>
      </header>

      <ul className="flex flex-col flex-1 gap-3 p-4 overflow-x-hidden overflow-y-auto">
        {conversation.messages.map((message, index) => {
          const previous = conversation.messages[index - 1];
          const isNewDay = !previous || dayOf(previous.created_at) !== dayOf(message.created_at);
          return (
            <React.Fragment key={message.id}>
              {isNewDay && (
                <li className="mx-auto my-2 text-xs font-semibold uppercase text-gray-1.0">
                  {dayOf(message.created_at)}
                </li>
              )}
              <li className="flex flex-col">
                <Entry message={message} />
              </li>
            </React.Fragment>
          );
        })}
        <li ref={bottom} />
      </ul>

      <Composer
        key={conversation.id}
        conversationId={conversation.id}
        greeting={needsGreeting ? FIRST_REPLY_GREETING : null}
        isSending={isSending}
        macros={macros}
        onSend={onSend}
        onSendNote={onSendNote}
        onSendFile={onSendFile}
        onManageMacros={onManageMacros}
      />
    </div>
  );
};

export default Thread;
